/**
 * Piezas presentacionales del reproductor de audios firmados. La lógica vive en
 * `useSignedAudioPlayer` (un solo <audio> dirigido por foco); aquí solo se pinta:
 *
 *  - `SharedAudio`  — el ÚNICO elemento <audio> del player (renderízalo una vez).
 *  - `PlayButton`   — botón ▶/⏸ por fila que dirige el player por id de ítem.
 *
 * Uso típico en una tabla (vista rápida / reporte): un `<PlayButton>` por fila y
 * un `<SharedAudio>` (sin `controls`, invisible) al pie. En la mesa de revisión
 * el <audio> se renderiza con `controls` como panel de foco.
 */
import { Button } from "./Button.js";
import type { SignedAudioPlayer } from "../hooks/useSignedAudioPlayer.js";

/** El único <audio> del player. `controls` muestra la barra nativa (panel de
 *  foco); sin `controls` queda invisible y se maneja por los botones de fila.
 *  `preload="none"`: un archivo corrupto/vencido NO se descarga en el montaje
 *  (no falla sin que el usuario toque play) — se busca recién al reproducir. */
export function SharedAudio({
  player,
  controls,
}: {
  player: SignedAudioPlayer;
  controls?: boolean;
}) {
  return (
    <audio
      {...player.audioProps}
      controls={controls ?? false}
      preload="none"
      style={{ maxWidth: "100%", ...(controls ? {} : { display: "none" }) }}
    />
  );
}

/** Botón ▶/⏸ por fila. Toca ▶ → reproduce; toca otro ▶ → detiene el anterior y
 *  reproduce el nuevo; toca el que suena → pausa. ✓ marca los ya reproducidos.
 *  Si el audio quedó marcado "no reproducible" (corrupto), el botón se deshabilita. */
export function PlayButton({
  player,
  id,
  label = "aria-play",
  noReproducibleLabel = "sin audio",
}: {
  player: SignedAudioPlayer;
  id: string;
  label?: string;
  noReproducibleLabel?: string;
}) {
  const activo = player.foco?.id === id;
  const sonando = activo && player.reproduciendo;
  const reproducido = player.reproducidos.has(id);
  const noReproducible = player.noReproducibles.has(id);
  if (noReproducible) {
    return (
      <Button variant="ghost" aria-label={noReproducibleLabel} disabled title={noReproducibleLabel}>
        ⚠
      </Button>
    );
  }
  return (
    <Button variant="ghost" aria-label={label} onClick={() => player.alternar(id)}>
      {sonando ? "⏸" : reproducido ? "✓ ▶" : "▶"}
    </Button>
  );
}
